import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useForm } from "react-hook-form";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { X, Plus, Trash2 } from "lucide-react";
import { HiOutlineTrash } from "react-icons/hi";
import ImageModal from "../Items Master/ImageModal";

const LocalPurchaseOrderModal = ({
  isOpen,
  closeModal,
  fetchLocalPurchaseOrders,
}) => {
  const [items, setItems] = useState([
    {
      itemCategory: "",
      itemName: "",
      itemCode: "",
      itemQuantity: 1,
      itemPrice: 0,
      itemImage: "",
      pcsPerCtn: 0,
    },
  ]);

  const [categories, setCategories] = useState([]);
  const [selectedCategoryItems, setSelectedCategoryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [clients, setClients] = useState([]);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const token = localStorage.getItem("token");

  const specialStyle = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: "28px",
      height: "28px",
      border: "1px solid #e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        border: "1px solid #3b82f6",
      },
      backgroundColor: "white",
      borderRadius: "0.375rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "28px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "28px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#94a3b8",
    }),
  };

  useEffect(() => {
    const fetchWarehouseData = async () => {
      try {
        const warehouseResponse = await axios.get(
          buildApiUrl("/api/warehouse/get-warehouse/name"),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setWarehouses(warehouseResponse.data.data);
      } catch (error) {
        console.error("Error fetching warehouse data:", error.message);
      }
    };
    fetchWarehouseData();
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientResponse = await axios.get(
          buildApiUrl("/api/clients"),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setClients(clientResponse.data);

        const categoryResponse = await axios.get(
          buildApiUrl("/api/categories/all-categories"),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setCategories(categoryResponse.data.categories);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };

    fetchData();
  }, [token]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const orderData = {
        clientId: data.clientId,
        warehouseName: selectedWarehouse,
        items,
      };

      await axios.post(
        buildApiUrl("/api/localpurchaseorder"),
        orderData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Local Purchase Order created successfully!");
      closeModal();
      fetchLocalPurchaseOrders();
      reset();
      setItems([
        {
          itemCategory: "",
          itemName: "",
          itemCode: "",
          itemQuantity: 1,
          itemPrice: 0,
          itemImage: "",
          pcsPerCtn: 0,
        },
      ]);
      setSelectedWarehouse("");
    } catch (error) {
      toast.error("Error creating order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        itemCategory: "",
        itemName: "",
        itemQuantity: 1,
        itemPrice: 0,
        itemImage: "",
        pcsPerCtn: 0,
      },
    ]);
  };

  const removeItem = (index) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setItems(updatedItems);
  };

  const handleItemChange = (index, event) => {
    const updatedItems = [...items];
    updatedItems[index][event.target.name] = event.target.value;
    setItems(updatedItems);
  };

  const handleCategoryChange = (index, option) => {
    const selectedCategory = option ? option.value : "";
    const category = categories.find(
      (cat) => cat.categoryName === selectedCategory
    );
    const itemsForCategory = category?.items || [];

    const updatedItems = [...items];
    updatedItems[index].itemCategory = selectedCategory;
    updatedItems[index].itemName = "";
    updatedItems[index].itemCode = "";
    updatedItems[index].itemImage = "";
    updatedItems[index].pcsPerCtn = "";
    setItems(updatedItems);

    setSelectedCategoryItems((prev) => {
      const updatedCategoryItems = [...prev];
      updatedCategoryItems[index] = itemsForCategory;
      return updatedCategoryItems;
    });
  };

  const handleItemNameChange = (index, option) => {
    const selectedItemName = option ? option.value : "";
    const selectedItem = (selectedCategoryItems[index] || []).find(
      (item) => item.itemName === selectedItemName
    );

    const updatedItems = [...items];
    updatedItems[index].itemName = selectedItemName;
    updatedItems[index].itemCode = selectedItem ? selectedItem.itemCode : "";
    updatedItems[index].itemImage = selectedItem ? selectedItem.itemImage : "";
    updatedItems[index].pcsPerCtn = selectedItem ? selectedItem.pcsPerCtn : 0;

    setItems(updatedItems);
  };

  function calculateTotalPrice() {
    return items
      .reduce((total, item) => total + item.itemQuantity * item.itemPrice, 0)
      .toFixed(2);
  }

  const handleCloseModal = () => {
    reset();
    setItems([
      {
        itemCategory: "",
        itemName: "",
        itemQuantity: 1,
        itemPrice: 0,
        itemImage: "",
        pcsPerCtn: 0,
      },
    ]);
    setSelectedWarehouse("");
    closeModal();
  };

  const clientOptions = clients.map((client) => ({
    value: client.user,
    label: `${client.firmName} - ${client.contactPerson}`,
  }));

  const warehouseOptions = warehouses.map((warehouse) => ({
    value: warehouse.warehouseName,
    label: warehouse.warehouseName,
  }));

  const categoryOptions = categories.map((category) => ({
    value: category.categoryName,
    label: category.categoryName,
  }));

  const getItemOptions = (index) =>
    (selectedCategoryItems[index] || []).map((item) => ({
      value: item.itemName,
      label: item.itemName,
    }));

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage("");
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div
            className="w-full max-w-6xl rounded-lg bg-white shadow-xl overflow-y-auto"
            style={{ maxHeight: "90vh", scrollbarWidth: "thin" }}
          >
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Create Local Purchase Order
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-4">
              <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Client <span className="text-red-600">*</span>
                  </label>
                  <Select
                    options={clientOptions}
                    value={
                      watch("clientId")
                        ? clientOptions.find(
                            (opt) => opt.value === watch("clientId")
                          )
                        : null
                    }
                    onChange={(option) =>
                      setValue("clientId", option ? option.value : "")
                    }
                    placeholder="Select Client"
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={specialStyle}
                    className="w-full"
                    required
                  />
                  {errors.clientId && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.clientId.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Warehouse <span className="text-red-600">*</span>
                  </label>
                  <Select
                    options={warehouseOptions}
                    value={
                      selectedWarehouse
                        ? warehouseOptions.find(
                            (opt) => opt.value === selectedWarehouse
                          )
                        : null
                    }
                    onChange={(option) =>
                      setSelectedWarehouse(option ? option.value : "")
                    }
                    placeholder="Select Warehouse"
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={specialStyle}
                    className="w-full"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <h3 className="mb-2 text-sm font-medium text-gray-700">
                  Items
                </h3>
                <div className="hidden sm:grid grid-cols-1 gap-4 text-xs tracking-wide py-2 font-medium bg-gray-200 text-gray-700 rounded-md mb-2 items-center sm:grid-cols-[1fr_1fr_repeat(3,_80px)_20px]">
                  <span className="pl-2">Category *</span>
                  <span>Item Name *</span>
                  <span>Item Image</span>
                  <span>Quantity *</span>
                  <span>Price *</span>
                  <span></span>
                </div>
                {items.map((item, index) => (
                  <div
                    key={index}
                    className="mb-2 rounded-md border sm:border-0 border-gray-200 bg-gray-50 p-3 sm:p-0"
                  >
                    <div className="grid grid-cols-1 gap-4 items-center sm:grid-cols-[1fr_1fr_repeat(3,_80px)_20px]">
                      <div>
                        <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                          Category
                        </label>
                        <Select
                          options={categoryOptions}
                          value={
                            item.itemCategory
                              ? categoryOptions.find(
                                  (opt) => opt.value === item.itemCategory
                                )
                              : null
                          }
                          onChange={(option) =>
                            handleCategoryChange(index, option)
                          }
                          placeholder="Select Category"
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={specialStyle}
                          className="w-full"
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                          Item Name
                        </label>
                        <Select
                          options={getItemOptions(index)}
                          value={
                            item.itemName
                              ? getItemOptions(index).find(
                                  (opt) => opt.value === item.itemName
                                )
                              : null
                          }
                          onChange={(option) =>
                            handleItemNameChange(index, option)
                          }
                          placeholder="Select Item"
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={specialStyle}
                          className="w-full"
                          required
                        />
                      </div>

                      <div>
                        <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                          Item Image
                        </label>
                        {item.itemImage ? (
                          <img
                            src={item.itemImage}
                            alt="Item Thumbnail"
                            className="size-6 rounded-md border border-gray-200 object-contain"
                            onClick={() => handleImageClick(item.itemImage)}
                          />
                        ) : (
                          <div className="size-4 rounded-md border border-gray-200 bg-gray-100"></div>
                        )}
                      </div>

                      <div>
                        <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                          Quantity
                        </label>
                        <input
                          type="number"
                          name="itemQuantity"
                          value={item.itemQuantity}
                          onChange={(e) => handleItemChange(index, e)}
                          required
                          className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block sm:hidden text-xs font-medium text-gray-700">
                          Price
                        </label>
                        <input
                          type="number"
                          name="itemPrice"
                          value={item.itemPrice}
                          onChange={(e) => handleItemChange(index, e)}
                          required
                          className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className=" flex items-center justify-center gap-1 text-xs text-red-600 hover:text-red-800"
                        >
                          <HiOutlineTrash size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mb-4 rounded-md bg-gray-50 p-3">
                <div className="flex justify-end gap-4 text-sm font-medium">
                  <span className="text-gray-700">Total Price:</span>
                  <span className="text-blue-600">{calculateTotalPrice()}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center justify-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
                >
                  <Plus size={16} />
                  Add Item
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-md bg-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:bg-indigo-400"
                >
                  {loading ? "Processing..." : "Create Order"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isImageModalOpen && (
        <ImageModal imageUrl={selectedImage} onClose={closeImageModal} />
      )}
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default LocalPurchaseOrderModal;
